import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatTime } from '@brimo/shared';
import { Button, Dialog, Pill, Skeleton, useToast } from '../../../../ds/components';
import { clsx } from '../../../../ds/clsx';
import { ApiError, api, errorMessage } from '../../../../lib/api';
import { useSavedPill } from '../../SettingsFrame';
import { organizationKeys, useDomain } from '../../../onboarding/admin/api';
import {
  CheckRows,
  RecordsTable,
  dayTime,
  domainCheckRows,
  listAddresses,
} from '../../../onboarding/admin/DomainStatusCards';
import { ItMailDialog } from '../../../onboarding/admin/ItMailDialog';
import { LoadError } from '../../../onboarding/admin/LoadError';
import { useDomainCheck } from '../../../onboarding/admin/steps/DomainStep';
import type { Domain, DomainAvailability, SendingDomain } from '../../../onboarding/admin/types';
import { WizardButton, WizardField } from '../../../onboarding/ui';
import { OrgSettingsPage } from '../brand/OrgSettingsPage';
import styles from './DomainSettingsPage.module.css';

const description = 'Das Mandantenportal läuft unter einer Subdomain Ihrer Firmendomain.';

/** Einstellungen 22–24 · Domain und Subdomain by `state`: none, waiting/verified/error (22, 23), active (24). */
export function DomainSettingsPage() {
  const domain = useDomain({ refetchInterval: 60_000 });
  if (domain.isPending) {
    return (
      <OrgSettingsPage title="Domain und Subdomain" description={description}>
        <div className={styles.grid}>
          <Skeleton variant="block" height={520} />
          <Skeleton variant="block" height={320} />
        </div>
      </OrgSettingsPage>
    );
  }
  if (domain.isError) {
    return (
      <OrgSettingsPage title="Domain und Subdomain" description={description}>
        <LoadError
          what="Die Domain konnte nicht geladen werden"
          error={domain.error}
          retrying={domain.isFetching}
          onRetry={() => void domain.refetch()}
        />
      </OrgSettingsPage>
    );
  }
  const data = domain.data;
  if (data.state === 'active') return <ActiveDomain domain={data} />;
  if (data.state === 'none') return <NoDomain domain={data} />;
  return <PendingDomain domain={data} />;
}

function useInterim() {
  const queryClient = useQueryClient();
  const { markSaved } = useSavedPill();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run() {
    setPending(true);
    setError(null);
    try {
      const next = await api<Domain>('/settings/organization/domain/interim', { method: 'POST', body: {} });
      queryClient.setQueryData(organizationKeys.domain, next);
      markSaved();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }
  return { run, pending, error };
}

/** „Kein eigener DNS-Zugang?" with „Übergangsadresse nutzen". */
function InterimCard({ domain }: { domain: Domain }) {
  const interim = useInterim();
  return (
    <section className={clsx(styles.card, styles.cardSmall)} aria-labelledby="domain-interim">
      <h3 id="domain-interim" className={clsx(styles.cardTitle, styles.cardTitleSmall)}>
        Kein eigener DNS-Zugang?
      </h3>
      {domain.interim.active ? (
        <p className={styles.text}>Das Portal läuft vorläufig unter {domain.interim.address}.</p>
      ) : (
        <>
          <p className={styles.text}>
            Wir können das Portal vorläufig unter {domain.interim.address} betreiben und später umstellen.
          </p>
          <WizardButton
            variant="secondary"
            size="small"
            className={styles.smallAction}
            loading={interim.pending}
            onClick={() => void interim.run()}
          >
            Übergangsadresse nutzen
          </WizardButton>
        </>
      )}
      {interim.error ? (
        <span className={styles.error} role="alert">
          {interim.error}
        </span>
      ) : null}
    </section>
  );
}

/** No reservation yet: „Gewünschte Adresse" with „Prüfen" and „Adresse reservieren" (own). */
function NoDomain({ domain }: { domain: Domain }) {
  const queryClient = useQueryClient();
  const { markSaved } = useSavedPill();
  const [subdomain, setSubdomain] = useState('portal');
  const [zone, setZone] = useState('');
  const [available, setAvailable] = useState<DomainAvailability | null>(null);
  const [fields, setFields] = useState<{ subdomain?: string | undefined; domain?: string | undefined }>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<'check' | 'reserve' | null>(null);

  async function run<T>(kind: 'check' | 'reserve', action: () => Promise<T>): Promise<T | null> {
    setBusy(kind);
    setFailure(null);
    setFields({});
    try {
      return await action();
    } catch (error) {
      if (error instanceof ApiError && (error.fields['subdomain'] || error.fields['domain'])) {
        setFields({ subdomain: error.fields['subdomain'], domain: error.fields['domain'] });
      } else {
        setFailure(errorMessage(error));
      }
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function check() {
    setAvailable(null);
    const result = await run('check', () =>
      api<DomainAvailability>('/settings/organization/domain/availability', {
        method: 'POST',
        body: { subdomain, domain: zone },
      }),
    );
    if (result) setAvailable(result);
  }

  async function reserve() {
    const next = await run('reserve', () =>
      api<Domain>('/settings/organization/domain', { method: 'PUT', body: { subdomain, domain: zone } }),
    );
    if (next) {
      queryClient.setQueryData(organizationKeys.domain, next);
      markSaved();
    }
  }

  return (
    <OrgSettingsPage title="Domain und Subdomain" description={description}>
      <div className={styles.grid}>
        <section className={styles.card} aria-labelledby="domain-address">
          <h3 id="domain-address" className={styles.cardTitle}>
            Gewünschte Adresse
          </h3>
          <div className={styles.hostRow}>
            <WizardField
              dense
              label="Subdomain"
              className={styles.subField}
              value={subdomain}
              spellCheck={false}
              onChange={(event) => {
                setSubdomain(event.target.value);
                setAvailable(null);
              }}
              error={fields.subdomain}
            />
            <span className={styles.dot} aria-hidden="true">
              .
            </span>
            <WizardField
              dense
              label="Ihre Domain"
              className={styles.zoneField}
              value={zone}
              spellCheck={false}
              placeholder="firma.de"
              onChange={(event) => {
                setZone(event.target.value);
                setAvailable(null);
              }}
              error={fields.domain}
            />
            <WizardButton
              variant="secondary"
              size="small"
              className={styles.checkButton}
              loading={busy === 'check'}
              onClick={() => void check()}
            >
              Prüfen
            </WizardButton>
          </div>
          {available ? (
            <>
              <div className={styles.success} role="status">
                {available.message}
              </div>
              <WizardButton
                variant="primary"
                size="small"
                className={styles.smallAction}
                loading={busy === 'reserve'}
                onClick={() => void reserve()}
              >
                Adresse reservieren
              </WizardButton>
            </>
          ) : null}
          {failure ? (
            <div className={styles.danger} role="alert">
              {failure}
            </div>
          ) : null}
        </section>
        <div className={styles.column}>
          <InterimCard domain={domain} />
        </div>
      </div>
    </OrgSettingsPage>
  );
}

function stepDetail(
  domain: Domain,
  key: Domain['steps'][number]['key'],
  fallback: string | null,
): string | null {
  if (key === 'it_mail' && domain.itMail) {
    return `An ${listAddresses(domain.itMail.recipients)} · ${dayTime(domain.itMail.sentAt)}`;
  }
  return fallback;
}

/** Einstellungen 22 (waiting), also the verified-without-TLS and error states. */
function PendingDomain({ domain }: { domain: Domain }) {
  const queryClient = useQueryClient();
  const check = useDomainCheck();
  const [mailOpen, setMailOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const pill =
    domain.state === 'error' ? (
      <Pill variant="danger" size="large" dot>
        Prüfung fehlgeschlagen
      </Pill>
    ) : domain.state === 'verified' ? (
      <Pill variant="info" size="large" dot>
        Einträge gefunden · Zertifikat folgt
      </Pill>
    ) : (
      <Pill variant="warning" size="large" dot>
        Wartet auf DNS-Eintrag
      </Pill>
    );

  async function provision() {
    setProvisioning(true);
    setProvisionError(null);
    try {
      const next = await api<Domain>('/settings/organization/domain/provision', { method: 'POST', body: {} });
      queryClient.setQueryData(organizationKeys.domain, next);
    } catch (failure) {
      setProvisionError(errorMessage(failure));
    } finally {
      setProvisioning(false);
    }
  }

  return (
    <OrgSettingsPage title="Domain und Subdomain" description={description} aside={pill}>
      {domain.state === 'error' && domain.lastError ? (
        <div className={styles.danger} role="alert">
          {domain.lastError}
          <WizardButton
            variant="secondary"
            size="small"
            className={styles.dangerAction}
            loading={check.checking}
            onClick={() => void check.check()}
          >
            Jetzt prüfen
          </WizardButton>
        </div>
      ) : null}
      <div className={styles.grid}>
        <div className={styles.column}>
          <section className={styles.card} aria-labelledby="domain-address">
            <div className={styles.cardHead}>
              <h3 id="domain-address" className={styles.cardTitle}>
                Gewünschte Adresse
              </h3>
              <WizardButton
                variant="ghost"
                size="small"
                className={styles.headAction}
                onClick={() => setChangeOpen(true)}
              >
                Adresse ändern
              </WizardButton>
            </div>
            <div className={styles.hostRow}>
              <WizardField
                dense
                label="Subdomain"
                className={styles.subField}
                value={domain.subdomain ?? ''}
                readOnly
              />
              <span className={styles.dot} aria-hidden="true">
                .
              </span>
              <WizardField
                dense
                label="Ihre Domain"
                className={styles.zoneField}
                value={domain.zone ?? ''}
                readOnly
              />
            </div>
            <div className={styles.target}>
              Ziel: <span className={styles.targetHost}>{domain.hostname}</span>
              <span className={styles.targetNote}>
                TLS-Zertifikat wird nach Freigabe automatisch ausgestellt
              </span>
            </div>
          </section>
          <section className={clsx(styles.card, styles.cardTight)} aria-labelledby="domain-steps">
            <div className={styles.cardHead}>
              <h3 id="domain-steps" className={styles.cardTitle}>
                Schritt für Schritt
              </h3>
              <span className={styles.aside}>für Ihre IT oder Ihren Domain-Anbieter</span>
            </div>
            <ol className={styles.steps}>
              {domain.steps.map((step, index) => (
                <li
                  key={step.key}
                  className={clsx(
                    styles.step,
                    step.status === 'current' && styles.stepCurrent,
                    step.status === 'open' && styles.stepOpen,
                  )}
                  aria-current={step.status === 'current' ? 'step' : undefined}
                >
                  <span className={styles.stepCircle} aria-hidden="true">
                    {step.status === 'done' ? '✓' : index + 1}
                  </span>
                  <div className={styles.stepBody}>
                    <span className={styles.stepTitle}>{step.label}</span>
                    {step.key === 'records' && step.status === 'current' ? (
                      <>
                        <div className={styles.recordsSmall}>
                          <RecordsTable records={domain.records} columns="80px 120px minmax(0, 1fr) 80px" />
                        </div>
                        {step.detail ? <span className={styles.stepNote}>{step.detail}</span> : null}
                      </>
                    ) : (
                      <span className={styles.stepDetail}>{stepDetail(domain, step.key, step.detail)}</span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            <div className={styles.actions}>
              {domain.provisioner.available ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.action, styles.actionPrimary)}
                  disabled={provisioning}
                  aria-busy={provisioning || undefined}
                  onClick={() => void provision()}
                >
                  Einträge automatisch setzen
                </button>
              ) : null}
              <button
                type="button"
                className={clsx('pressable', styles.action)}
                disabled={check.checking}
                aria-busy={check.checking || undefined}
                onClick={() => void check.check()}
              >
                Jetzt prüfen
              </button>
              <button
                type="button"
                className={clsx('pressable', styles.action, styles.actionQuiet)}
                onClick={() => setMailOpen(true)}
              >
                {domain.itMail ? 'Anleitung erneut senden' : 'Anleitung an IT senden'}
              </button>
              <a
                className={clsx('pressable', styles.action, styles.actionQuiet)}
                href="/api/settings/organization/domain/guide.pdf"
                download
              >
                Als PDF exportieren
              </a>
            </div>
            {(check.error ?? provisionError) ? (
              <span className={styles.error} role="alert">
                {check.error ?? provisionError}
              </span>
            ) : null}
          </section>
        </div>
        <div className={styles.column}>
          <section className={styles.status} aria-labelledby="domain-status">
            <span className={styles.statusEyebrow}>Prüfstatus</span>
            <h3 id="domain-status" className={styles.statusTitle}>
              {domain.lastCheckedAt
                ? `Letzte Prüfung ${formatTime(domain.lastCheckedAt)}`
                : 'Noch nicht geprüft'}
            </h3>
            <CheckRows rows={domainCheckRows(domain, false)} className={styles.statusChecks} />
            {domain.lastError && domain.state !== 'error' ? (
              <span className={styles.statusError}>{domain.lastError}</span>
            ) : null}
            <p className={styles.statusText}>
              {domain.nextCheckAt
                ? `Nächste automatische Prüfung um ${formatTime(domain.nextCheckAt)}. `
                : ''}
              Sie werden per E-Mail informiert, sobald die Subdomain aktiv ist.
            </p>
          </section>
          <InterimCard domain={domain} />
        </div>
      </div>
      <ItMailDialog open={mailOpen} onClose={() => setMailOpen(false)} variant="settings" />
      <ChangeAddressDialog domain={domain} open={changeOpen} onClose={() => setChangeOpen(false)} />
    </OrgSettingsPage>
  );
}

/** „Adresse ändern" (own confirmation): removes the reservation; the page returns to „Gewünschte Adresse". */
function ChangeAddressDialog({
  domain,
  open,
  onClose,
}: {
  domain: Domain;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function confirm() {
    setPending(true);
    setError(null);
    try {
      const next = await api<Domain>('/settings/organization/domain', { method: 'DELETE' });
      queryClient.setQueryData(organizationKeys.domain, next);
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Domain und Subdomain"
      title="Adresse ändern?"
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={pending} onClick={() => void confirm()}>
            Reservierung aufheben
          </Button>
        </>
      }
    >
      <p>
        Die Reservierung von {domain.hostname} wird aufgehoben. Links auf das Mandantenportal laufen bis zur
        neuen Adresse wieder über die bisherige Adresse. Danach legen Sie eine neue Adresse fest.
      </p>
      {error ? (
        <span className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </Dialog>
  );
}

/** Einstellungen 24: the active host, CNAME, TXT and the sending domain („Mailversand"). */
function ActiveDomain({ domain }: { domain: Domain }) {
  const [changeOpen, setChangeOpen] = useState(false);
  const cname = domain.records.find((record) => record.type === 'CNAME');
  const txt = domain.records.find((record) => record.type === 'TXT');
  const verified = domain.verifiedAt
    ? `Verifiziert am ${formatDate(domain.verifiedAt)}, ${formatTime(domain.verifiedAt)}`
    : '';
  const tls = domain.checks.tls.validUntil
    ? `TLS-Zertifikat gültig bis ${formatDate(domain.checks.tls.validUntil)}`
    : '';
  return (
    <OrgSettingsPage title="Domain und Subdomain" description={description} gap={20}>
      <section className={styles.activeCard} aria-labelledby="domain-active">
        <span className={styles.activeIcon} aria-hidden="true">
          ✓
        </span>
        <div className={styles.activeText}>
          <h3 id="domain-active" className={styles.activeTitle}>
            {domain.hostname} ist aktiv
          </h3>
          <span className={styles.activeSub}>{[verified, tls].filter(Boolean).join(' · ')}</span>
        </div>
        <div className={styles.activeActions}>
          {domain.portalUrl ? (
            <a
              className={clsx('pressable', styles.action)}
              href={domain.portalUrl}
              target="_blank"
              rel="noreferrer"
            >
              Portal öffnen
            </a>
          ) : null}
          <button
            type="button"
            className={clsx('pressable', styles.action, styles.actionQuiet)}
            onClick={() => setChangeOpen(true)}
          >
            Adresse ändern
          </button>
        </div>
      </section>
      <div className={styles.facts}>
        <section className={styles.fact} aria-label="CNAME">
          <span className={styles.factLabel}>CNAME</span>
          <span className={styles.factValue}>{domain.checks.cname ? 'Gefunden' : 'Nicht gefunden'}</span>
          {cname ? (
            <span className={styles.factDetail}>
              {cname.name} → {cname.value}
            </span>
          ) : null}
        </section>
        <section className={styles.fact} aria-label="TXT-Verifizierung">
          <span className={styles.factLabel}>TXT-Verifizierung</span>
          <span className={styles.factValue}>{domain.checks.txt ? 'Bestätigt' : 'Offen'}</span>
          {txt ? <span className={styles.factDetail}>{txt.value}</span> : null}
        </section>
        <MailFact />
      </div>
      <ChangeAddressDialog domain={domain} open={changeOpen} onClose={() => setChangeOpen(false)} />
    </OrgSettingsPage>
  );
}

/** „Mailversand": B3's sending domain — verified: label and sender; otherwise its records with „Werte kopieren". */
function MailFact() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const sending = useQuery({
    queryKey: organizationKeys.sendingDomain,
    queryFn: () => api<SendingDomain>('/settings/organization/sending-domain'),
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function setup(domain: string) {
    setPending(true);
    setError(null);
    try {
      const next = await api<SendingDomain>('/settings/organization/sending-domain', {
        method: 'PUT',
        body: { domain },
      });
      queryClient.setQueryData(organizationKeys.sendingDomain, next);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }

  async function copy(records: SendingDomain['records']) {
    try {
      await navigator.clipboard.writeText(
        records.map((record) => `${record.type} · ${record.name} · ${record.value}`).join('\n'),
      );
      toast.toast({ message: 'Werte kopiert.' });
    } catch {
      setError('Die Werte konnten nicht in die Zwischenablage kopiert werden.');
    }
  }

  const data = sending.data;
  return (
    <section className={styles.fact} aria-label="Mailversand">
      <span className={styles.factLabel}>Mailversand</span>
      {sending.isPending ? (
        <Skeleton width={140} height={14} />
      ) : sending.isError || !data ? (
        <span className={styles.factDetail}>{errorMessage(sending.error)}</span>
      ) : (
        <>
          <span className={styles.factValue}>{data.statusLabel}</span>
          {data.status === 'verified' && data.sender ? (
            <span className={styles.factDetail}>Absender {data.sender}</span>
          ) : data.domain ? (
            <>
              <ul className={styles.mailRecords}>
                {data.records.map((record) => (
                  <li key={`${record.type}-${record.name}-${record.value}`}>
                    {record.type} · {record.name} · {record.value}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={clsx('pressable', styles.action, styles.factAction)}
                onClick={() => void copy(data.records)}
              >
                Werte kopieren
              </button>
            </>
          ) : data.suggestion ? (
            <button
              type="button"
              className={clsx('pressable', styles.action, styles.factAction)}
              disabled={pending}
              aria-busy={pending || undefined}
              onClick={() => void setup(data.suggestion ?? '')}
            >
              Absender {data.suggestion} einrichten
            </button>
          ) : null}
        </>
      )}
      {error ? (
        <span className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </section>
  );
}
